import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { cn } from '@/lib/utils';

interface SearchInputProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  onLoadingChange?: (isLoading: boolean) => void;
  onCommitSearch?: (term: string) => void; // Called when user presses Enter
  showAsChip?: boolean; // When true, search input is more minimal since search is shown as chip
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onLoadingChange,
  onCommitSearch,
  showAsChip = false
}, ref) => {
  // Local state for immediate UI responsiveness
  const [localValue, setLocalValue] = useState(searchValue);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isTypingRef = useRef(false); // Track if user is actively typing

  // Expose the input ref to parent
  useImperativeHandle(ref, () => inputRef.current!, []);

  // Update local value ONLY when prop changes externally AND user is not typing
  useEffect(() => {
    // Only sync from prop if user is not actively typing
    if (!isTypingRef.current && searchValue !== localValue) {
      console.log(`📝 [SearchInput] External change detected, syncing:`, {
        from: localValue,
        to: searchValue
      });
      setLocalValue(searchValue);
      setIsLoading(false);
    }
  }, [searchValue]);

  // Notify parent when loading state changes
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Simple debouncing for search
  useEffect(() => {
    // Clear previous timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Skip very short searches
    if (localValue.length > 0 && localValue.length < 2) {
      console.log(`⏸️ [SearchInput] Search too short (${localValue.length} chars), waiting for more input`);
      setIsLoading(false);
      return;
    }

    // Only search if different from current search value
    if (localValue !== searchValue) {
      console.log(`⏳ [SearchInput] Debouncing search:`, {
        localValue,
        currentSearchValue: searchValue,
        willSearchIn: '300ms'
      });
      setIsLoading(true);
      timerRef.current = setTimeout(() => {
        console.log(`🔍 [SearchInput] Debounce complete, calling onSearchChange("${localValue}")`);
        isTypingRef.current = false; // User stopped typing
        onSearchChange(localValue);
        setIsLoading(false);
      }, 300);
    } else {
      // Values match - stop loading indicator
      setIsLoading(false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [localValue, searchValue, onSearchChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log(`⌨️ [SearchInput] User typing: "${newValue}"`);
    isTypingRef.current = true; // Mark as actively typing
    setLocalValue(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && localValue.trim()) {
      console.log(`⏎ [SearchInput] Enter pressed, committing: "${localValue}"`);
      // Clear any pending debounce timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // Immediately apply the search and commit it as a chip
      isTypingRef.current = false;
      onSearchChange(localValue);
      onCommitSearch?.(localValue);
      setIsLoading(false);
    }
  };

  // Adjust placeholder when search is shown as chip
  const effectivePlaceholder = showAsChip && localValue.trim() === '' 
    ? 'Verfijn zoeken...' 
    : searchPlaceholder;

  return (
    <input
      ref={inputRef}
      value={localValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={effectivePlaceholder}
      className={cn(
        "flex-1 min-w-[160px] bg-transparent border-0 text-sm outline-none placeholder:text-muted-foreground",
        "focus:outline-none focus:ring-0"
      )}
    />
  );
});
